// src/pages/Ads/Regist/AdsRegistSelf4.jsx

import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import SectionTitle from "../../../assets/components/TitleGroup";
import { SingleButton } from "../../../assets/components/ButtonGroup";
import PlaceholderB from "../../../assets/components/PlaceholderB";
import search from "../../../assets/icons/System/search_default.svg";
import cancel from "../../../assets/icons/System/cancel.svg";
import info_red from "../../../assets/icons/warning/info_red.svg";
import { useFixedLayout } from "../../../hooks/useFixedLayout";

const AdsRegistSelf4 = ({
    goToStep,
    setStep5Items,
    setStep7Seed,
    targetStep5,
    targetStep7,
}) => {
    const [storeName, setStoreName] = useState("");
    const [zonecode, setZonecode] = useState(""); // 우편번호
    const [roadAddress, setRoadAddress] = useState(""); // 도로명 주소
    const [detailAddress, setDetailAddress] = useState("");
    const [loading, setLoading] = useState(false);
    const [storeNameTouched, setStoreNameTouched] = useState(false);

    const detailRef = useRef(null);
    const storeNameRef = useRef(null);

    const navigate = useNavigate();
    const location = useLocation();

    const { footerRef, bottomSpace } = useFixedLayout();

    // 원래 쓰던 minHeight 계산 그대로 유지
    // const contentMinHeight = bottomSpace
    //     ? `calc(100dvh - ${bottomSpace}px - 60px)`
    //     : "100dvh";

    // 버튼 위에 여유를 두기 위한 더미 영역 높이 (bottomSpace - 16px)
    // const spacerHeight = Math.max((bottomSpace || 0) - 16, 0);

    const PAGES_URL = "https://jyesapps.github.io/kakaomap/";

    // 공용 열기 함수
    const openPostcodeUniversal = async () => {
        const url = `${PAGES_URL}?inapp=1`;
        navigate(`/inapp/address?url=${encodeURIComponent(url)}`, {
            state: { background: location, modal: true, returnTo: location },
        });
    };

    // replace 복귀 시 state로도 값 수신 (이벤트 폴백)
    useEffect(() => {
        const r = location.state?.postcodeResult;
        if (!r) return;
        if (r.road) setRoadAddress(r.road);
        if (r.zip) setZonecode(r.zip);
        setTimeout(() => detailRef.current?.focus(), 0);
    }, [location.state?.postcodeResult]);

    // 페이지 진입 시 가게 이름 자동 선택 + 키보드 오픈
    useEffect(() => {
        const timer = setTimeout(() => {
            if (storeNameRef.current) {
                storeNameRef.current.focus();
            }
        }, 200);

        return () => clearTimeout(timer);
    }, []);

    // 유효성
    const isValid =
        storeName.trim().length > 0 &&
        zonecode.trim().length > 0 &&
        roadAddress.trim().length > 0;

    // “검색/다음” 클릭
    const handleSubmit = async () => {
        if (!isValid) {
            alert("가게 이름과 주소를 입력해 주세요.");
            return;
        }
        setLoading(true);

        const payload = { store_name: storeName.trim(), road_name: roadAddress };

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_FASTAPI_ADS_URL}/ads/store/match`,
                payload,
                { headers: { "Content-Type": "application/json" } }
            );

            const data = res.data;

            // 성공 케이스: result === "success" 이고 store_info가 배열
            if (data?.result === "success" && Array.isArray(data.store_info)) {
                const items = data.store_info;

                // Step5용 아이템 세팅
                setStep5Items(items);

                // Step7Seed는 지금 입력한 값 기준으로 저장
                setStep7Seed({
                    storeName: storeName.trim(),
                    zonecode,
                    roadAddress, // 사용자가 선택한 주소 그대로
                    detailAddress,
                });

                goToStep(targetStep5);
            } else {
                // 실패 케이스: result === false,
                // store_info: { ROAD_NAME_ADDRESS: "..." }
                const fallbackRoadAddress =
                    data?.store_info?.ROAD_NAME_ADDRESS || roadAddress;

                setStep7Seed({
                    storeName: storeName.trim(),
                    zonecode,
                    roadAddress: fallbackRoadAddress,
                    detailAddress,
                });

                goToStep(targetStep7);
            }
        } catch (err) {
            console.error("Search error:", err);
            alert(
                `매장 검색 중 오류:\n${err?.response?.data?.detail || err?.message}`
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col h-full">
            {/* 내용 영역 */}
            <div className="flex-1 flex flex-col px-[16px] pt-[16px]">
                <SectionTitle
                    title={"매장 정보 검색"}
                    subTitle={"wizMarket을 이용하기 위해서는 매장 정보가 필요해요."}
                    className={"pb-[40px]"}
                />

                {/* 위쪽 폼 + 아래 경고문구를 flex로 분리 */}
                <div className="flex-1 flex flex-col">
                    {/* 가게 이름 */}
                    <div className="pb-[24px]">
                        <PlaceholderB
                            ref={storeNameRef}
                            label="가게 이름"
                            placeholder="예) 서울 커피 대치점"
                            required={true}
                            value={storeName}
                            onChange={(e) => setStoreName(e.target.value)}
                            onBlur={() => setStoreNameTouched(true)}
                            validation={(value) => {
                                const trimmed = (value ?? "").trim();

                                if (!storeNameTouched) {
                                    return { status: "default", message: "" };
                                }

                                if (trimmed.length === 0) {
                                    return {
                                        status: "error",
                                        message: "가게 이름을 입력해 주세요.",
                                    };
                                }

                                return { status: "default", message: "" };
                            }}
                            autoComplete="name"
                            rightIconMode={"clear"}
                            rightIcon={<img src={cancel} alt="삭제" />}
                        />
                    </div>

                    {/* 주소 섹션 */}
                    <div className="space-y-[12px]">
                        <PlaceholderB
                            label="주소"
                            placeholder="우편번호"
                            required={true}
                            mode="click"
                            value={zonecode}
                            onClick={openPostcodeUniversal}
                            rightIconMode="static"
                            rightIcon={<img src={search} alt="검색" />}
                        />
                        <PlaceholderB
                            placeholder="기본 주소"
                            mode="display"
                            value={roadAddress}
                        />
                        <PlaceholderB
                            ref={detailRef}
                            placeholder="상세 주소"
                            value={detailAddress}
                            onChange={(e) => setDetailAddress(e.target.value)}
                            autoComplete="street-address"
                            rightIconMode={"clear"}
                            rightIcon={<img src={cancel} alt="삭제" />}
                        />
                    </div>

                    {/* 아래 영역: 경고문구 + 버튼 높이만큼 더미 영역 */}
                    <div className="mt-auto">
                        <div className="pt-[16px] text-c2 text-text-danger flex gap-[4px]">
                            <img src={info_red} alt="경고" className="w-[16px] h-[17px]" />
                            <p>
                                본인과 관계 없는 사업장을 등록하는 경우 이용 제한과 법적 제재를 받을 수 있습니다.
                            </p>
                        </div>
                        <div style={{ height: Math.max(bottomSpace, 0) }} />
                    </div>
                </div>
            </div>

            {/* 하단 fixed 버튼 */}
            <div
                ref={footerRef}
                className="fixed bottom-0 left-0 right-0 bg-surface-default"
            >
                <SingleButton onClick={handleSubmit} disabled={loading || !isValid}>
                    {loading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                        "검색하기"
                    )}
                </SingleButton>
            </div>
        </div>
    );
};

export default AdsRegistSelf4;
